/** @format — Documents list infinite query hook */

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/services/python-api/queryKeys";
import { listDocuments } from "@/services/python-api/documentsService";
import type {
  CustomDateRange,
  DocumentDateFilter,
  DocumentItem,
  DocumentSortBy,
  DocumentSortOrder,
  DocumentsListResponse,
  ListDocumentsParams,
} from "../types";
import { dateFilterToRange, mapApiDocumentToItem } from "../utils/documentFormatters";

export const DOCUMENTS_PAGE_SIZE = 20;

function toApiParams({
  search,
  sortBy,
  sortOrder,
  dateFilter,
  customDateRange,
  listingId,
  page = 0,
  limit = DOCUMENTS_PAGE_SIZE,
}: {
  search: string;
  sortBy: DocumentSortBy;
  sortOrder: DocumentSortOrder;
  dateFilter: DocumentDateFilter | string;
  customDateRange?: CustomDateRange;
  listingId?: string;
  page?: number;
  limit?: number;
}): ListDocumentsParams {
  const { startDate, endDate } = dateFilterToRange(dateFilter, customDateRange);
  return {
    search: search.trim() || undefined,
    startDate,
    endDate,
    page,
    limit,
    listingId,
    sortBy: sortBy === "listing" ? "listing" : "date_created",
    orderBy: sortOrder,
  };
}

function applyClientFilters(items: DocumentItem[], { type }: { type: string }) {
  if (!type || type === "all") return items;
  return items.filter((item) => String(item.uploadType).toLowerCase() === type.toLowerCase());
}

function getNextDocumentsPage(lastPage: DocumentsListResponse, allPages: DocumentsListResponse[]) {
  const batch = Array.isArray(lastPage?.data) ? lastPage.data.length : 0;
  const loaded = allPages.reduce(
    (sum, page) => sum + (Array.isArray(page?.data) ? page.data.length : 0),
    0,
  );
  const total = lastPage?.total;

  if (batch < DOCUMENTS_PAGE_SIZE) return undefined;
  if (typeof total === "number" && loaded >= total) return undefined;

  return allPages.length;
}

export function useDocumentsList({
  search,
  type,
  dateFilter,
  customDateRange,
  sortBy,
  sortOrder,
  listingId,
  enabled = true,
}: {
  search: string;
  type: string;
  dateFilter: DocumentDateFilter | string;
  customDateRange?: CustomDateRange;
  sortBy: DocumentSortBy;
  sortOrder: DocumentSortOrder;
  listingId?: string;
  enabled?: boolean;
}) {
  const listParams = useMemo(
    () =>
      toApiParams({
        search,
        sortBy,
        sortOrder,
        dateFilter,
        customDateRange,
        listingId,
        limit: DOCUMENTS_PAGE_SIZE,
      }),
    [search, sortBy, sortOrder, dateFilter, customDateRange, listingId],
  );

  const query = useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCUMENTS, "list", listParams, type],
    enabled: enabled && sortBy !== "listing",
    queryFn: async ({ pageParam = 0 }): Promise<DocumentsListResponse> => {
      try {
        return await listDocuments({ ...listParams, page: pageParam });
      } catch {
        return { data: [], total: 0, success: true };
      }
    },
    initialPageParam: 0,
    getNextPageParam: getNextDocumentsPage,
    staleTime: 30_000,
    retry: false,
  });

  const documents = useMemo(() => {
    const raw =
      query.data?.pages?.flatMap((page) => (Array.isArray(page?.data) ? page.data : [])) ?? [];
    const seen = new Set<string>();
    const mapped: DocumentItem[] = [];

    for (const item of raw) {
      const doc = mapApiDocumentToItem(item);
      if (seen.has(doc.id)) continue;
      seen.add(doc.id);
      mapped.push(doc);
    }

    return applyClientFilters(mapped, { type });
  }, [query.data, type]);

  const total = query.data?.pages?.[0]?.total ?? documents.length;

  return {
    documents,
    total,
    filteredCount: documents.length,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    loadMore: query.fetchNextPage,
    refetch: query.refetch,
  };
}
